import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { Convert, GraphData } from "../core/samples/graph-data";
import {
  Observable,
  Operator,
  OperatorFunction,
  catchError,
  map,
  shareReplay,
} from "rxjs";
import { GraphType } from "#/declarations";

/**
 * Service to handle loading data from JSON files.  Displays a toast error on
 * error.
 * TODO: Cache data loaded from files in local storage.
 * TODO: Check local storage for cached data before making a request for
 *    for the file.
 */
@Injectable()
export class JsonDataService {
  private caches: Record<string, Observable<GraphData[]>>;
  private dataPath: string = "assets/data/";

  constructor(
    private http: HttpClient,
    public toastCtrl: ToastController,
  ) {
    this.caches = {};
  }

  /**
   * Fetches a file (from cache if possible) and return parsed JSON graph data
   */
  public loadGraphData(
    sampleId: string,
    type: GraphType,
  ): Observable<GraphData[]> {
    const key = this.keyFor(sampleId, type);

    if (!this.caches[key]) {
      this.caches[key] = this.loadFile(this.filePathFor(sampleId, type)).pipe(
        shareReplay(1),
      );
    }

    return this.caches[key];
  }

  private keyFor(sampleId: string, type: GraphType) {
    return sampleId + "-" + type;
  }

  private filePathFor(sampleId: string, type: GraphType) {
    return this.dataPath + sampleId + "/" + type + ".json";
  }

  /**
   * RxJs catchError() handler factory
   */
  protected createErrorToast() {
    return (error: unknown) => {
      this.toastCtrl
        .create({
          message: String(error),
          duration: 5000,
        })
        .then((toast) => toast.present());

      throw error;
    };
  }

  /**
   * RxJs pipe operator factory: parse graph data JSON text and sort the entries
   */
  protected parseGraphData(): OperatorFunction<string, GraphData[]> {
    return (source: Observable<string>) =>
      source.pipe(
        map((jsonText) =>
          Convert.toGraphData(jsonText).sort((a, b) => a.x - b.x),
        ),
      );
  }

  /**
   * Loads the JSON file that is passed to it.
   * @returns HTTP promise
   */
  loadFile(file: string): Observable<GraphData[]> {
    const request = this.http
      .get(file, { responseType: "text" })
      .pipe(this.parseGraphData(), catchError(this.createErrorToast()));

    return request;
  }
}
